import { useEffect, useState, type ReactNode } from 'react';
import { useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';

export function FinancialFormProtection({
  dirty,
  pending,
  onClose,
  children,
}: {
  dirty: boolean;
  pending: boolean;
  onClose: () => void;
  children: (close: () => void) => ReactNode;
}) {
  const { t } = useTranslation();
  const blocker = useBlocker(dirty);
  const [discard, setDiscard] = useState(false);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const close = () => {
    if (pending) return;
    if (dirty) setDiscard(true);
    else onClose();
  };
  return (
    <>
      {children(close)}
      <AlertDialog open={discard || blocker.state === 'blocked'} onOpenChange={setDiscard}>
        <AlertDialogContent>
          <AlertDialogTitle>{t('ledger.unsavedTitle')}</AlertDialogTitle>
          <AlertDialogDescription>{t('ledger.unsavedHint')}</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={pending}
              onClick={() => {
                setDiscard(false);
                if (blocker.state === 'blocked') blocker.reset();
              }}
            >
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={pending}
              onClick={() => {
                setDiscard(false);
                if (blocker.state === 'blocked') blocker.proceed();
                else onClose();
              }}
            >
              {t('ledger.discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
